import { useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { PROJECTS } from '../../constants';
import { Reveal, TextReveal } from '../../components/Reveal';

const FEATURED = [
  {
    ...PROJECTS[0],
    category: 'Mobile application',
    summary: 'A store assistant for inventory, transactions, and daily sales insights.',
    stack: ['Kotlin', 'Java', 'PostgreSQL'],
    mark: 'P',
    theme: 'green',
  },
  {
    ...PROJECTS[1],
    category: 'AI learning tool',
    summary: 'An AI-powered mentor that makes data structures and algorithms easier to explore.',
    stack: ['React', 'Java', 'PostgreSQL'],
    mark: 'A',
    theme: 'coral',
  },
  {
    ...PROJECTS[2],
    category: 'Independent project',
    summary: 'A solo project in progress. More details will be shared as the build takes shape.',
    stack: [],
    mark: 'U',
    theme: 'violet',
  },
];

const CARD_POSITIONS = ['center', 'right', 'left'];

export const ProjectsSection = ({ onOpenPaninda, onOpenAlgoSensei }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const reduceMotion = useReducedMotion();

  const moveSpotlight = (event) => {
    if (reduceMotion || event.pointerType !== 'mouse') return;
    const card = event.currentTarget;
    const bounds = card.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width;
    const y = (event.clientY - bounds.top) / bounds.height;
    card.style.setProperty('--tilt-x', `${(0.5 - y) * 5}deg`);
    card.style.setProperty('--tilt-y', `${(x - 0.5) * 5}deg`);
    card.style.setProperty('--spot-x', `${x * 100}%`);
    card.style.setProperty('--spot-y', `${y * 100}%`);
  };

  const resetSpotlight = (event) => {
    const card = event.currentTarget;
    card.style.removeProperty('--tilt-x');
    card.style.removeProperty('--tilt-y');
    card.style.removeProperty('--spot-x');
    card.style.removeProperty('--spot-y');
  };

  return (
    <section id="projects" className="pf-section pf-projects-section">
      <div className="pf-container">
        <div className="pf-section-heading pf-section-heading-split">
          <div><Reveal as="span" className="pf-section-index" distance={6}>01 / Projects</Reveal><TextReveal delay={0.08}>Selected <em>work</em></TextReveal></div>
        </div>

        <div className="pf-project-deck" aria-label="Featured projects">
          {FEATURED.map((project, index) => {
            const openProject = index === 0 ? onOpenPaninda : index === 1 ? onOpenAlgoSensei : null;
            return (
              <Reveal
                as="article"
                distance={0}
                delay={index * 0.1}
                key={project.id}
                className={`pf-deck-card pf-deck-card-${project.theme}`}
                data-position={CARD_POSITIONS[index]}
                data-active={activeIndex === index}
                role={openProject ? 'button' : 'group'}
                tabIndex={0}
                aria-label={openProject ? `Explore ${project.title}` : `${project.title}, in progress`}
                onPointerEnter={() => setActiveIndex(index)}
                onPointerDown={() => setActiveIndex(index)}
                onPointerMove={moveSpotlight}
                onPointerLeave={resetSpotlight}
                onFocus={() => setActiveIndex(index)}
                onClick={openProject || undefined}
                onKeyDown={(event) => {
                  if (!openProject || !['Enter', ' '].includes(event.key)) return;
                  event.preventDefault();
                  openProject();
                }}
              >
                <div className="pf-deck-card-surface">
                  <div className="pf-deck-card-top">
                    <span className="pf-deck-card-number">{project.num} / {project.category}</span>
                    <span className="pf-deck-card-status">{project.status === 'completed' ? 'Completed' : 'In progress'}</span>
                  </div>
                  <div className="pf-deck-card-identity">
                    <span className="pf-deck-card-mark" aria-hidden="true">{project.mark}</span>
                    <div><h3>{project.title}</h3><span>{project.role}</span></div>
                  </div>
                  <p className="pf-deck-card-summary">{project.summary}</p>
                  <div className="pf-deck-card-bottom">
                    <div className="pf-deck-card-tags">{project.stack.map((tag) => <span key={tag}>{tag}</span>)}</div>
                    <span className="pf-deck-card-cta">{openProject ? 'View case study ↗' : 'Building in progress'}</span>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal as="p" className="pf-deck-hint" distance={8}><span className="pf-deck-hint-desktop">Hover or focus to bring a card forward</span><span className="pf-deck-hint-mobile">Swipe to explore projects</span> <span aria-hidden="true">↗</span></Reveal>

        <div className="pf-more-projects">
          <Reveal className="pf-more-title" distance={8}><span className="pf-section-index">More work</span><span>04 — 05</span></Reveal>
          {PROJECTS.slice(3).map((project, index) => (
            <Reveal className="pf-more-row" key={project.id} delay={index * 0.08} distance={12}>
              <span>{project.num}</span>
              <div><h3>{project.title}</h3><p>{project.role}</p></div>
              <span>{project.status === 'completed' ? 'Completed' : 'In progress'}</span>
              {project.href ? <a href={project.href} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${project.title}`}>↗</a> : <span aria-hidden="true">—</span>}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
